import { createContext, type PropsWithChildren, useContext, useEffect, useState } from 'react';

import { type DeviceMode, resolveDeviceMode } from './deviceMode';

const DeviceModeContext = createContext<DeviceMode | null>(null);

const readDeviceMode = (): DeviceMode => {
  if (typeof navigator === 'undefined') return 'desktop';

  return resolveDeviceMode({
    environment: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      maxTouchPoints: navigator.maxTouchPoints,
    },
    search: typeof window === 'undefined' ? '' : window.location.search,
  });
};

export const DeviceModeProvider = ({ children }: PropsWithChildren) => {
  const [deviceMode] = useState(readDeviceMode);

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    document.documentElement.dataset.deviceMode = deviceMode;
    return () => {
      delete document.documentElement.dataset.deviceMode;
    };
  }, [deviceMode]);

  return <DeviceModeContext.Provider value={deviceMode}>{children}</DeviceModeContext.Provider>;
};

// The provider and hook intentionally share this module's private context.
// eslint-disable-next-line react-refresh/only-export-components
export const useDeviceMode = (): DeviceMode => {
  const deviceMode = useContext(DeviceModeContext);
  if (!deviceMode) {
    throw new Error('useDeviceMode must be used within DeviceModeProvider');
  }

  return deviceMode;
};
